import { FC, useState } from 'react';
import { Row, Col, Flex } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';
import { TRtkErrorType } from 'data/types/generic.types';
import { EWorkshopSeriesType } from 'data/types/workshops.types';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import useForm from 'hooks/useForm';
import { useAppDispatch } from 'redux/store';
import { registrantsApi } from 'redux/apiSlice/registrants.slice';
import { useAddWorkshopRegistrationMutation } from 'redux/apiSlice/workshops.slice';
import useGetValidationData from 'components/Base/Form/validationRules';
import {
  formatPhoneWhileTyping,
  optionalPhoneValidator,
} from 'helpers/phoneUtils';
import { isFormValidationError } from 'helpers/formUtils';
import { Modal } from 'components/Base/Modal';
import { Form, FormItem, FormList } from 'components/Base/Form';
import { Input, TextArea } from 'components/Base/Input';
import Button from 'components/Base/Button/Button';
import { DeleteTrashOutline } from 'assets/icon/icon';
import {
  TAddWorkshopRegistrationForm,
  TAddWorkshopRegistrationModalProps,
} from './AddRegistrationModal.types';
import SessionSelect from './SessionSelect';

const AddRegistrationModal: FC<TAddWorkshopRegistrationModalProps> = ({
  open,
  setOpen,
  workshopId,
  workshop,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { showToastNotification } = useShowToastNotification();
  const { validationRules } = useGetValidationData();
  const { form } = useForm<TAddWorkshopRegistrationForm>();
  const [addWorkshopRegistration, { isLoading }] =
    useAddWorkshopRegistrationMutation();
  const [phoneValue, setPhoneValue] = useState('');

  // GROW-4362 minimal form. Field visibility mirrors
  // WorkshopAggregate.validateRegistration:
  // - hasReadyBinder is not asked; it is sent as `false` for
  //   READY_FOR_KINDERGARTEN and omitted for every other workshop type.
  // - childNames are optional for non-CCP workshops and never sent for CCP.
  const isReadyForKindergarten =
    workshop.type === EWorkshopSeriesType.READY_FOR_KINDERGARTEN;
  const collectsChildNames = !workshop.isCCP;
  const customQuestion = workshop.customQuestion?.value;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneWhileTyping(e.target.value);
    setPhoneValue(formatted);
    form.setFieldValue('phone', formatted);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const {
        workshopClassId,
        firstName,
        lastName,
        email,
        phone,
        childNames,
        customQuestionAnswer,
      } = values;

      await addWorkshopRegistration({
        workshopId,
        workshopClassId,
        firstName,
        lastName,
        email,
        phone,
        hasReadyBinder: isReadyForKindergarten ? false : undefined,
        childNames: collectsChildNames ? childNames : undefined,
        customQuestionAnswer: customQuestion
          ? customQuestionAnswer?.trim() || undefined
          : undefined,
      }).unwrap();

      // Refresh the Registrants, Attendance, and Waitlist tables on the
      // details page. The add-registration mutation lives in workshopsApi
      // (which cannot import registrantsApi without a circular import), so
      // we invalidate the shared tags from here where both APIs are already
      // in scope. `workshop-registrants` feeds both the Registrants and
      // Attendance tabs; `wait-list` feeds the Waitlist tab (a new
      // registration can affect wait-list ordering / promotion).
      dispatch(
        registrantsApi.util.invalidateTags([
          { type: 'workshop-registrants', id: workshopId },
          'wait-list',
        ]),
      );

      showToastNotification({
        message: 'Registration added successfully!',
        type: ENotificationType.SUCCESS,
      });

      setOpen(false);
    } catch (e) {
      if (isFormValidationError(e)) {
        return;
      }

      showToastNotification({
        message:
          (e as TRtkErrorType)?.data?.message || 'Failed to add registration',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setPhoneValue('');
    setOpen(false);
  };

  return (
    <Modal
      title="Add Registration"
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Add"
      cancelText="Cancel"
      confirmLoading={isLoading}
      width={600}
      destroyOnClose
      centered
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark
        initialValues={{
          ...(collectsChildNames && { childNames: [''] }),
        }}
      >
        <FormItem
          name="workshopClassId"
          label="Session"
          rules={[validationRules.required('Session')]}
          required
        >
          <SessionSelect classes={workshop.classes.value} />
        </FormItem>
        <Row gutter={24}>
          <Col span={12}>
            <FormItem
              name="firstName"
              label="First Name"
              rules={[validationRules.required('First Name')]}
              required
            >
              <Input />
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem
              name="lastName"
              label="Last Name"
              rules={[validationRules.required('Last Name')]}
              required
            >
              <Input />
            </FormItem>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <FormItem
              name="email"
              label="Email"
              rules={[validationRules.email()]}
            >
              <Input />
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem
              name="phone"
              label="Phone"
              rules={[{ validator: optionalPhoneValidator }]}
            >
              <Input
                value={phoneValue}
                onChange={handlePhoneChange}
                placeholder="(555) 555-5555"
              />
            </FormItem>
          </Col>
        </Row>
        {collectsChildNames && (
          <FormList name="childNames">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <FormItem
                    key={field.key}
                    label={index === 0 ? 'Child Name' : ''}
                    style={{ marginBottom: 8 }}
                  >
                    <Flex gap={12} align="center">
                      <FormItem {...field} name={field.name} noStyle>
                        <Input placeholder="Child Name" />
                      </FormItem>
                      {/* Fixed-width trailing slot so every row's input has
                          the same width, regardless of Add / Delete button. */}
                      <div style={{ width: 120, flexShrink: 0 }}>
                        {index === 0 ? (
                          <Button
                            variant="link"
                            onClick={() => add('')}
                            icon={<PlusOutlined />}
                          >
                            Add Child
                          </Button>
                        ) : (
                          <Button
                            variant="link"
                            onClick={() => remove(field.name)}
                            icon={
                              <DeleteTrashOutline
                                size={14}
                                fill={theme.colorPrimaryDefault}
                              />
                            }
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </Flex>
                  </FormItem>
                ))}
              </>
            )}
          </FormList>
        )}
        {customQuestion && (
          <FormItem name="customQuestionAnswer" label={customQuestion}>
            <TextArea maxLength={144} showCount />
          </FormItem>
        )}
      </Form>
    </Modal>
  );
};

export default AddRegistrationModal;
